import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MENUITEM_IDS } from '@/lib';
import { StanButton, StanInput } from '@/components';
import { useGlobalStore } from '@/stores';
import { useStanRouter } from '@/hooks/useStanRouter';
import {
    DEFAULT_FUNNEL_TEMPLATE_ID,
    FUNNEL_TEMPLATES,
    type FunnelTemplateId,
} from '@/features/funnels/constants/funnelTemplates';
import { FunnelTemplateCard } from '@/features/funnels/components/create/FunnelTemplateCard';
import { useCreateFunnel } from '@/features/funnels/queries/useFunnelMutations';

const createFunnelSchema = z.object({
    name: z.string().trim().min(1, 'Funnel name is required'),
});

type CreateFunnelFormData = z.infer<typeof createFunnelSchema>;

const CreateFunnelPage = () => {
    const { push } = useStanRouter();
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);

    const createFunnel = useCreateFunnel();
    const [selectedTemplate, setSelectedTemplate] = useState<FunnelTemplateId>(DEFAULT_FUNNEL_TEMPLATE_ID);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<CreateFunnelFormData>({
        resolver: zodResolver(createFunnelSchema),
        defaultValues: { name: 'Funnel - Draft' },
    });

    useEffect(() => {
        setBreadcrumbs([
            { title: 'My Funnels', to: '/funnels', id: MENUITEM_IDS.FUNNELS },
            { title: 'Create Funnel', to: '', id: 'funnel-create', active: true },
        ]);
    }, [setBreadcrumbs]);

    const onSubmit = async (data: CreateFunnelFormData) => {
        const result = await createFunnel.mutateAsync({ name: data.name, template: selectedTemplate });
        if (result?.store_id) {
            push(`/funnels/${result.store_id}`);
        }
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className='mx-auto flex w-full max-w-screen-lg flex-col gap-10 pb-24 lg:pb-36'
        >
            <header className='flex flex-col gap-2'>
                <h2 className='text-dark'>Create New Funnel</h2>
                <p className='text-body-md text-gray'>
                    Give your funnel a name and pick a starting template. You can always edit, add or remove pages
                    later.
                </p>
            </header>

            <section className='flex flex-col gap-3'>
                <h3 className='text-heading-h3 text-dark'>Name</h3>
                <Controller
                    name='name'
                    control={control}
                    render={({ field }) => (
                        <StanInput
                            id='new-funnel-name'
                            name={field.name}
                            placeholder='e.g. Black Friday Funnel'
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={!!errors.name}
                            hintText={errors.name?.message}
                        />
                    )}
                />
            </section>

            <section className='flex flex-col gap-4'>
                <div className='flex flex-col gap-1'>
                    <h3 className='text-heading-h3 text-dark'>Template</h3>
                    <p className='text-body-sm text-gray'>
                        Pick the starting point that fits your use case. Every template is fully editable.
                    </p>
                </div>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    {FUNNEL_TEMPLATES.map(template => (
                        <FunnelTemplateCard
                            key={template.id}
                            template={template}
                            selected={selectedTemplate === template.id}
                            onSelect={() => setSelectedTemplate(template.id)}
                        />
                    ))}
                </div>
            </section>

            <div className='flex items-center justify-end gap-3'>
                <StanButton
                    name='cancel-create-funnel'
                    variant='outline'
                    type='button'
                    onClick={() => push('/funnels')}
                >
                    Cancel
                </StanButton>
                <StanButton
                    name='submit-create-funnel'
                    type='submit'
                    disabled={createFunnel.isPending}
                    loading={createFunnel.isPending}
                >
                    Create Funnel
                </StanButton>
            </div>
        </form>
    );
};

export default CreateFunnelPage;
